import type { ConditionalVisibility, FieldShortcut } from 'korint-sdk/react';
import {
  BriefcaseIcon,
  FlagIcon,
  HashIcon,
  LandmarkIcon,
  MailIcon,
  PhoneIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';

const showWhenManualEntry = [
  {
    fieldName: 'isManualCompanyEntry',
    condition: 'equals',
    value: true,
  },
] as const satisfies ConditionalVisibility[];

const showWhenSiretSearch = [
  {
    fieldName: 'isManualCompanyEntry',
    condition: 'notEquals',
    value: true,
  },
] as const satisfies ConditionalVisibility[];

// The SIREN lookup leaves a field out rather than blank when INSEE has no value for it; an empty
// manual input must do the same, so the two flows produce the same keys.
const optionalText = (value: unknown): string | undefined => {
  const trimmed = typeof value === 'string' ? value.trim() : '';

  return trimmed === '' ? undefined : trimmed;
};

const legalStatusLevel =
  (level: '1' | '2' | '3') =>
  (firm: Record<string, unknown>): string | undefined => {
    const { legalStatus } = (firm.companyInfo ?? {}) as { legalStatus?: Record<string, string> };

    return legalStatus?.[level];
  };

// `brokerageFirmCustomFields` carries no `requiredFor`, so `required` can't be inferred.
export const informationFields: Array<FieldShortcut> = [
  {
    name: 'isManualCompanyEntry',
    required: false,
    type: 'checkbox',
    ignoreInPayload: true,
  },
  {
    name: 'siret',
    required: true,
    type: 'company',
    apiDataAccessor: 'companyInfo',
    formDataAccessor: 'siret.siret',
    showWhen: showWhenSiretSearch,
  },
  {
    name: 'manualCompanyName',
    required: true,
    type: 'text',
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { name?: string } | undefined)?.name,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanySiret',
    required: true,
    type: 'text',
    minLength: 14,
    maxLength: 14,
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { siret?: string } | undefined)?.siret,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyAddress',
    required: true,
    type: 'text',
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { location?: { address?: string } } | undefined)?.location?.address,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyPostalCode',
    required: true,
    type: 'postcode',
    countries: ['FR'],
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { location?: { postCode?: string } } | undefined)?.location?.postCode,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyCity',
    required: true,
    type: 'text',
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { location?: { city?: string } } | undefined)?.location?.city,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyCreationDate',
    required: true,
    type: 'date',
    max: 'now',
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { creationDate?: string } | undefined)?.creationDate,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyLegalStatus2',
    required: true,
    type: 'text',
    apiDataAccessor: legalStatusLevel('2'),
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyLegalStatus1',
    required: false,
    type: 'text',
    apiDataAccessor: legalStatusLevel('1'),
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyLegalStatus3',
    required: false,
    type: 'text',
    apiDataAccessor: legalStatusLevel('3'),
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyNafCode',
    required: false,
    type: 'text',
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { nafCode?: string } | undefined)?.nafCode,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyNafLabel',
    required: false,
    type: 'text',
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { nafLabel?: string } | undefined)?.nafLabel,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyHeadcount',
    required: false,
    type: 'text',
    apiDataAccessor: (firm: Record<string, unknown>) =>
      (firm.companyInfo as { headcount?: string } | undefined)?.headcount,
    ignoreInPayload: true,
    showWhen: showWhenManualEntry,
  },
  {
    name: 'manualCompanyInfo',
    type: 'computed',
    required: false,
    hidden: true,
    dependsOn: [
      'isManualCompanyEntry',
      'manualCompanyName',
      'manualCompanySiret',
      'manualCompanyAddress',
      'manualCompanyPostalCode',
      'manualCompanyCity',
      'manualCompanyCreationDate',
      'manualCompanyLegalStatus1',
      'manualCompanyLegalStatus2',
      'manualCompanyLegalStatus3',
      'manualCompanyNafCode',
      'manualCompanyNafLabel',
      'manualCompanyHeadcount',
    ],
    formDataAccessor: (form: Record<string, unknown>) => {
      if (form.isManualCompanyEntry !== true) return {};
      const siret = optionalText(form.manualCompanySiret);
      const legalStatus = {
        1: optionalText(form.manualCompanyLegalStatus1),
        2: optionalText(form.manualCompanyLegalStatus2),
        3: optionalText(form.manualCompanyLegalStatus3),
      };
      return {
        companyInfo: {
          name: form.manualCompanyName,
          siret,
          siren: siret ? siret.slice(0, 9) : undefined,
          nafCode: optionalText(form.manualCompanyNafCode),
          nafLabel: optionalText(form.manualCompanyNafLabel),
          creationDate: optionalText(form.manualCompanyCreationDate),
          headcount: optionalText(form.manualCompanyHeadcount),
          // A firm being onboarded is trading; INSEE has no answer to mirror here.
          isActive: true,
          legalStatus: Object.values(legalStatus).some(Boolean) ? legalStatus : undefined,
          location: {
            address: form.manualCompanyAddress,
            postCode: form.manualCompanyPostalCode,
            city: form.manualCompanyCity,
            country: 'France',
          },
        },
      };
    },
  },
  {
    name: 'manualCompanySiretCustomField',
    type: 'computed',
    required: false,
    hidden: true,
    isCustomField: true,
    payloadKeyName: 'siret',
    dependsOn: ['isManualCompanyEntry', 'manualCompanySiret'],
    formDataAccessor: (form: Record<string, unknown>) =>
      form.isManualCompanyEntry === true ? optionalText(form.manualCompanySiret) : undefined,
  },
  { name: 'orias', required: true, minLength: 8, maxLength: 8, icon: HashIcon },
  { name: 'email', required: true, icon: MailIcon },
  { name: 'phoneNumber', required: true, icon: PhoneIcon },
  { name: 'employeeCount', required: true, icon: UsersIcon },
  { name: 'ceoFirstName', required: true, icon: UserIcon },
  { name: 'ceoLastName', required: true, icon: UserIcon },
  { name: 'ceoEmail', required: true, icon: MailIcon },
  { name: 'ceoPhoneNumber', required: true, icon: PhoneIcon },
  { name: 'ceoPosition', required: true, icon: BriefcaseIcon },
  { name: 'ceoNationality', required: true, icon: FlagIcon },
];

export const contactInformationFields: Array<FieldShortcut> = [
  { name: 'contactFirstName', required: true, icon: UserIcon },
  { name: 'contactLastName', required: true, icon: UserIcon },
  { name: 'contactEmail', required: true, icon: MailIcon },
  { name: 'contactPhoneNumber', required: true, icon: PhoneIcon },
];

export const paymentMethodFields: Array<FieldShortcut> = [
  { name: 'iban', required: true, icon: LandmarkIcon },
];
